import clsx from 'clsx';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { COOKIE_CATEGORIES } from '../../config/cookie-consent.config';
import { Button } from '@/shared/components/Buttons/Button/Button';
import type { CookieConsentBannerProps } from './CookieConsentBanner.types';

export function CookieConsentBanner({ className }: CookieConsentBannerProps) {
  const {
    showBanner,
    isCustomizing,
    customPreferences,
    acceptAll,
    toggleCustomize,
    updateCategory,
    saveCustom,
  } = useCookieConsent();

  if (!showBanner) return null;

  return (
    <div
      className={clsx(
        'fixed right-0 bottom-0 left-0 z-9999 touch-manipulation border-t border-gray-200 bg-white shadow-2xl',
        className
      )}
      role='dialog'
      aria-modal='false'
      aria-label='Cookie consent'
      aria-describedby='cookie-consent-description'
    >
      <div className='mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='flex items-start gap-3'>
          <Shield className='mt-0.5 h-5 w-5 shrink-0 text-blue-600' />
          <div className='min-w-0 flex-1'>
            <h2 className='text-base font-semibold text-gray-900'>We value your privacy</h2>
            <p id='cookie-consent-description' className='mt-1 text-sm text-gray-600'>
              We use cookies and similar technologies to enhance your experience, analyze site
              traffic, and personalize content. By clicking 'Accept All', you consent to the use of
              all cookies. You can manage your preferences at any time.
            </p>
          </div>
        </div>

        {/* Customize panel */}
        {isCustomizing && (
          <div className='mt-4 space-y-3 border-t border-gray-100 pt-4'>
            {COOKIE_CATEGORIES.map(category => (
              <div
                key={category.id}
                className='flex items-center justify-between gap-4 rounded-lg bg-gray-50 p-3'
              >
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium text-gray-900'>{category.label}</span>
                    {category.required && (
                      <span className='rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700'>
                        Always on
                      </span>
                    )}
                  </div>
                  <p className='mt-0.5 text-xs text-gray-500'>{category.description}</p>
                </div>
                <label className='relative inline-flex shrink-0 cursor-pointer items-center'>
                  <input
                    type='checkbox'
                    className='peer sr-only'
                    checked={customPreferences[category.id]}
                    disabled={category.required}
                    onChange={e => updateCategory(category.id, e.target.checked)}
                    aria-label={`${category.label} cookies`}
                  />
                  <div
                    className={clsx(
                      'h-6 w-11 rounded-full after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[""]',
                      'peer-checked:bg-blue-600 peer-checked:after:translate-x-full',
                      'peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                      !customPreferences[category.id] && 'bg-gray-300'
                    )}
                  />
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end'>
          <div className='flex flex-col gap-2 sm:flex-row'>
            <Button
              variant='link'
              size='small'
              onClick={toggleCustomize}
              aria-expanded={isCustomizing}
              rightIcon={
                isCustomizing ? (
                  <ChevronDown className='h-4 w-4' />
                ) : (
                  <ChevronUp className='h-4 w-4' />
                )
              }
            >
              {isCustomizing ? 'Hide details' : 'Customize'}
            </Button>
            {isCustomizing && (
              <Button variant='outline' size='small' onClick={saveCustom}>
                Save Preferences
              </Button>
            )}
            <Button variant='primary' size='small' onClick={acceptAll}>
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
